import React, { useCallback, useMemo, useState } from 'react';
import cx from 'classnames';
import { Button, Typography } from '@navi/web-ui/lib/primitives';
import styles from './RecommendedModal.module.scss';
import SparkleIcon from '@cp/src/assets/icons/SparkleIcon';
import CloseIcon from '@navi/web-ui/lib/icons/CloseIcon';
import { useDispatch } from 'react-redux';
import { closeRecommendedModal } from '../reducer';
import {
  fetchCaseRecommendationCases,
  fetchCaseRecommendationCurrentCase,
  fetchRefreshCaseRecommendation,
  fetchCaseRecommendationMetadata,
  fetchLatestCurrentCase
} from '../caseRecommendationActions';
import {
  DEFAULT_LIMIT_FOR_RECOMMENDED_CASES,
  DEFAULT_OFFSET_FOR_RECOMMENDED_CASES
} from '../utils';
import { useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { DateFormat, getFormatDate } from '@cp/src/utils/DateHelper';
import CaseListContainer from './CaseListContainer';
import CaseRecommendationIcon from '@cp/src/assets/icons/CaseRecommendationIcon';
import { StartResumeCalling } from '../types';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { navigateToCase } from '../utils';

interface RecommendedModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const RecommendedModal: React.FC<RecommendedModalProps> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();

  const [currentOffset, setCurrentOffset] = useState(DEFAULT_OFFSET_FOR_RECOMMENDED_CASES);

  const { caseList } = useSelector((state: RootState) => state.caseRecommendation.recommendedCases);
  const recommendedCasesMetadata = useSelector(
    (state: RootState) => state.caseRecommendation.recommendedMetadata
  );
  const isLoading = useSelector(
    (state: RootState) => state.caseRecommendation.isLoadingRecommendedCases
  );

  const onRefresh = () => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_CASE_RECOMMENDATION_REFRESH_CLICK);
    setCurrentOffset(DEFAULT_OFFSET_FOR_RECOMMENDED_CASES);

    const successCallback = () => {
      dispatch(
        fetchCaseRecommendationCases({
          limit: DEFAULT_LIMIT_FOR_RECOMMENDED_CASES,
          offset: DEFAULT_OFFSET_FOR_RECOMMENDED_CASES
        })
      );
    };
    dispatch(
      fetchRefreshCaseRecommendation({
        limit: DEFAULT_LIMIT_FOR_RECOMMENDED_CASES,
        offset: DEFAULT_OFFSET_FOR_RECOMMENDED_CASES,
        successCallback
      })
    );
    dispatch(fetchCaseRecommendationMetadata());
  };

  const handleStartCalling = () => {
    addClickstreamEvent(
      CLICKSTREAM_EVENT_NAMES.LH_CASE_RECOMMENDATION_START_OR_RESUME_CALLING_CLICK
    );
    dispatch(closeRecommendedModal());
    recommendedCasesMetadata.visitedCases === 0
      ? dispatch(fetchCaseRecommendationCurrentCase(navigateToCase))
      : dispatch(fetchLatestCurrentCase(navigateToCase));
  };

  const lastRefreshedTime = getFormatDate(
    new Date(recommendedCasesMetadata.lastRefreshedInMs || 0),
    DateFormat.hh_mm_ss_ampm
  );
  const startOrResumeCallingText = () => {
    if (recommendedCasesMetadata.visitedCases === 0) {
      return StartResumeCalling.START;
    } else if (recommendedCasesMetadata.visitedCases < recommendedCasesMetadata.totalCases) {
      return StartResumeCalling.RESUME;
    }
    return StartResumeCalling.REFRESH;
  };
  const isStartButtonDisabled = useMemo(() => {
    const visitedCases = recommendedCasesMetadata.visitedCases;
    const totalCases = recommendedCasesMetadata.totalCases;
    return !totalCases || visitedCases >= totalCases - 1;
  }, [recommendedCasesMetadata]);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={cx(styles.modal, 'flex flex-col h-[80vh] max-h-[80vh]')}
        onClick={e => e.stopPropagation()}
      >
        <div
          className={cx(
            'flex flex-row justify-between items-center px-6 flex-shrink-0',
            styles.header
          )}
        >
          <div className="flex justify-between items-start">
            <div className="flex flex-col gap-1">
              <Typography variant="h3" color="var(--blue-dark)" className="font-extrabold">
                Recommended For You
              </Typography>
              <div className="flex flex-row items-center gap-2">
                <Typography variant="p4" color="var(--grayscale-2)">
                  Last refreshed at {lastRefreshedTime}
                </Typography>
                <Button
                  variant="text"
                  color="var(--blue-base)"
                  className="underline"
                  onClick={onRefresh}
                  disabled={isLoading}
                >
                  {isLoading ? 'Refreshing...' : 'Refresh'}
                </Button>
              </div>
            </div>
          </div>
          <div className="flex flex-row items-start gap-2">
            <CaseRecommendationIcon width={80} height={80} />
            <Button variant="text" onClick={onClose} className="pt-4">
              <CloseIcon />
            </Button>
          </div>
        </div>

        <div className="flex-1 flex flex-col min-h-0">
          {caseList?.length ? (
            <div className="mx-6 mt-6 mb-[1px] border-[1px] border-solid border-[var(--greyscale-light)] bg-[var(--bg-primary)] flex-1 flex flex-col min-h-0">
              <div className="grid grid-cols-[1fr_2fr] gap-4 px-6 py-4 bg-[var(--bg-secondary)] border-b border-x-0 border-t-0 border-solid border-[var(--greyscale-light)] flex-shrink-0">
                <Typography variant="p4" color="var(--grayscale-3)">
                  Customer Name
                </Typography>
                <Typography variant="p4" color="var(--grayscale-3)">
                  Why this Case?
                </Typography>
              </div>

              <div className="flex-1 overflow-hidden">
                <CaseListContainer
                  currentOffset={currentOffset}
                  setCurrentOffset={setCurrentOffset}
                />
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <Typography
                variant="p3"
                color="var(--grayscale-2)"
                className="flex items-center justify-center p-6 mb-8"
              >
                {isLoading ? 'Loading recommended cases...' : 'No recommended cases available'}
              </Typography>
            </div>
          )}
        </div>

        <div className="flex-shrink-0">
          <Button
            variant="text"
            className="w-full h-[48px] bg-[var(--blue-base)] rounded-none text-[var(--bg-primary)]"
            onClick={handleStartCalling}
            startAdornment={<SparkleIcon />}
            disabled={isStartButtonDisabled}
          >
            {startOrResumeCallingText()}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RecommendedModal;
